/* ============================================================
   storage — the URL is the state.

   Where you are, what you have selected, what is open in the
   preview and how far into it you are all live in the query
   string, so a refresh lands where you were and a pasted link
   takes someone else to the same place:

     ?view=all                 which of the four views
     &d=hp                     the drive
     &p=/CLIPS/Destiny 2       the folder being browsed
     &q=raid                   what is typed in the filter
     &sel=f:<id>,d:<id>        what is selected
     &file=<id>                what is open in the preview
     &t=137                    where the player is, in whole seconds
     &viewer=1                 …and that this tab is only the viewer

   One scheme, two readers: the pane inside the browser and the
   standalone viewer page differ by `viewer` alone. Defaults are
   left out, so the plain address stays plain.

   Nothing here touches history — StorageApp decides between
   replaceState (a position, a filter being typed) and pushState
   (entering a folder, opening a file), because that choice is
   what makes Back mean something.
   ============================================================ */

export const HOME = { view: 'home', drive: 'disinteg', path: '/' };
/* `home` is what a bare address opens. `search` is every file you can see,
   flat and filterable; it used to be a view called `all`, and links to that
   still land here. */
const VIEWS = ['home', 'files', 'search', 'dashboard', 'admin', 'trash'];

/* A link has to survive being pasted into a chat window, and a hundred
   selected files is 3,500 characters of ids. Past this many the link says
   how many there were and the keys go to sessionStorage, which follows a
   refresh in the same tab and — rightly — does not follow the link. */
export const SEL_MAX = 20;
const SEL_STORE = 'st.url.sel';

const normPath = (p) => {
  if (!p) return '/';
  const s = String(p).replace(/\/+$/, '');
  return s.startsWith('/') ? (s || '/') : `/${s}`;
};

/* ── the overflow selection ──────────────────────────────────────────── */

const selScope = (drive, path) => `${drive}\u0000${normPath(path)}`;

function writeOverflow(drive, path, keys) {
  try {
    // One folder's worth: the point is to survive a refresh, not to keep a
    // history of everything ever selected.
    window.sessionStorage.setItem(SEL_STORE, JSON.stringify({ scope: selScope(drive, path), keys }));
  } catch { /* private mode; the link still says how many there were */ }
}

function readOverflow(drive, path) {
  try {
    const raw = JSON.parse(window.sessionStorage.getItem(SEL_STORE) || 'null');
    return raw && raw.scope === selScope(drive, path) && Array.isArray(raw.keys) ? raw.keys : [];
  } catch { return []; }
}

/** `sel` as it goes into the link: the keys themselves, or `+137` when there
 *  are too many for one — the set itself goes to sessionStorage. */
export function encodeSelection(selection, drive, path) {
  const keys = Array.from(selection || []);
  if (!keys.length) return null;
  if (keys.length <= SEL_MAX) return keys.join(',');
  writeOverflow(drive, path, keys);
  return `+${keys.length}`;
}

/** The other direction. `lost` is a link that named more than it could carry,
 *  opened somewhere that never had the rest — worth saying out loud. */
export function decodeSelection(raw, drive, path) {
  if (!raw) return { selection: new Set(), lost: 0 };
  if (raw.startsWith('+')) {
    const claimed = Number(raw.slice(1)) || 0;
    const keys = readOverflow(drive, path);
    return { selection: new Set(keys), lost: keys.length ? 0 : claimed };
  }
  return { selection: new Set(raw.split(',').filter(Boolean)), lost: 0 };
}

/* ── reading and writing the query string ────────────────────────────── */

export function parseUrl(search) {
  const q = new URLSearchParams(search ?? (typeof window === 'undefined' ? '' : window.location.search));
  const drive = q.get('d') || HOME.drive;
  const path = normPath(q.get('p'));
  const t = Number(q.get('t'));
  const { selection, lost } = decodeSelection(q.get('sel'), drive, path);
  // No view named: a link that names a place (a drive, a folder, a file, a
  // selection) is the file list, as every link was before Home existed; a
  // bare address is Home.
  const named = q.get('view') === 'all' ? 'search' : q.get('view');
  const somewhere = ['d', 'p', 'file', 'sel', 'q'].some((k) => q.has(k));
  return {
    view: VIEWS.includes(named) ? named : (somewhere ? 'files' : HOME.view),
    drive,
    path,
    query: q.get('q') || '',
    selection,
    lostSelection: lost,
    file: q.get('file') || null,
    // Whole seconds. A link written by hand with `t=90.5` still means 90.
    at: Number.isFinite(t) && t > 0 ? Math.floor(t) : 0,
    // `viewer=1` is the standalone tab; `file` on its own opens the pane.
    // Links made before the pane could be linked to carried neither, and
    // land on the file in its folder, which is the better half of what they
    // promised.
    viewer: q.get('viewer') === '1',
  };
}

/** The address bar for a state: path plus query, defaults left out. */
export function buildUrl(st, base) {
  const q = new URLSearchParams();
  const pathname0 = base ?? (typeof window === 'undefined' ? '/' : window.location.pathname);
  // Home is the bare address and carries nothing.
  if (st.view === 'home' || !st.view) return pathname0;
  if (st.view !== 'files') q.set('view', st.view);
  if (st.drive && st.drive !== HOME.drive) q.set('d', st.drive);
  if (st.path && st.path !== HOME.path) q.set('p', st.path);
  if (st.query) q.set('q', st.query);
  const sel = encodeSelection(st.selection, st.drive || HOME.drive, st.path || HOME.path);
  if (sel) q.set('sel', sel);
  if (st.file) {
    q.set('file', st.file);
    if (st.at > 0) q.set('t', String(Math.floor(st.at)));
    if (st.viewer) q.set('viewer', '1');
  }
  // The file list at the top of the default drive names nothing else, and
  // would read as Home: say which it is.
  if (st.view === 'files' && ![...q.keys()].length) q.set('view', 'files');
  const pathname = pathname0;
  // '/', ':' and ',' are all legal in a query string, and the folder is the
  // part people read, so they are left as themselves rather than as %2F, %3A
  // and %2C.
  const s = q.toString().replace(/%2F/g, '/').replace(/%3A/g, ':').replace(/%2C/g, ',');
  return s ? `${pathname}?${s}` : pathname;
}

/** An absolute link to one file — the viewer tab, or the pane it sits in. */
export function fileUrl(fileId, seconds, { viewer = true, drive, path } = {}) {
  const base = typeof window === 'undefined' ? '/' : window.location.pathname;
  const origin = typeof window === 'undefined' ? '' : window.location.origin;
  return origin + buildUrl({
    ...HOME,
    view: 'files',
    drive: drive || HOME.drive,
    path: path || HOME.path,
    file: fileId,
    at: Number.isFinite(seconds) && seconds > 1 ? Math.floor(seconds) : 0,
    viewer,
  }, base);
}
